/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { getCookieValue, setSessionCookieValue } from '@/Client/Lib/cookieLib';
import { isMobileDevice } from '@/Client/Lib/isMobileDevice';
import { useAuth } from '@/Client/State';
import { AlertIcon } from '@kaiizen/kai-shiki/react';
import { MODAL_BASE_PRIMARY_BUTTON_STYLE, ModalBase } from '../ModalBase';
import { DEFAULT_MOBILE_WEB_WARNING_STYLES, type MobileWebWarningStyles } from './mobileWebWarningStyles';

export const MOBILE_WEB_WARNING_ACKNOWLEDGED_COOKIE_KEY = 'mobileWebWarningAcknowledged';
export const MOBILE_WEB_WARNING_TITLE = 'Mobile web is not recommended for field work';
export const MOBILE_WEB_WARNING_RISK_TEXT =
  'Photo uploads can fail or be permanently lost if the connection drops or the browser closes, refreshes, or goes into the background.';
export const MOBILE_WEB_WARNING_GUIDANCE_TEXT =
  'Use the KaiPro iPad app whenever possible. If you must use the web, stay on strong Wi-Fi and keep Kai open with the screen on until all photos finish uploading.';
export const MOBILE_WEB_WARNING_ACKNOWLEDGE_LABEL = 'I understand';

const isAcknowledged = () => !!getCookieValue(MOBILE_WEB_WARNING_ACKNOWLEDGED_COOKIE_KEY);

const shouldShowMobileWebWarning = () => isMobileDevice() && !isAcknowledged();

/**
 * Warns phone and tablet users that the web app is not safe for field photo capture.
 * Acknowledging sets a session cookie, so the warning stays away until the browser fully
 * restarts. On phones that can take days: mobile browsers are rarely quit, and tab
 * restore keeps session cookies alive. The cookie is shared across tabs, so a tab that was
 * already open re-checks it when it comes back into view and hides if another tab has
 * already acknowledged.
 */
export const MobileWebWarning = ({ styles = DEFAULT_MOBILE_WEB_WARNING_STYLES }: { styles?: MobileWebWarningStyles }) => {
  const [visible, setVisible] = useState(shouldShowMobileWebWarning);
  const { credentials } = useAuth();
  // Passed explicitly: this mounts on the same commit as App's setUserContext effect, and
  // React runs a child's effects first, so the registered org/user context isn't set yet.
  const analyticsContext = { orgId: credentials.orgId, userId: credentials.user?.id };

  useEffect(() => {
    if (visible) trackEvent(ANALYTICS_EVENTS.mobileWebWarningShown, analyticsContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the initial showing counts
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const hideIfAcknowledgedElsewhere = () => {
      if (document.visibilityState === 'visible' && isAcknowledged()) setVisible(false);
    };
    document.addEventListener('visibilitychange', hideIfAcknowledgedElsewhere);
    return () => document.removeEventListener('visibilitychange', hideIfAcknowledgedElsewhere);
  }, [visible]);

  if (!visible) return null;

  const acknowledge = () => {
    setSessionCookieValue(MOBILE_WEB_WARNING_ACKNOWLEDGED_COOKIE_KEY, '1');
    setVisible(false);
    trackEvent(ANALYTICS_EVENTS.mobileWebWarningAcknowledged, analyticsContext);
  };

  return (
    <ModalBase
      icon={<AlertIcon />}
      title={MOBILE_WEB_WARNING_TITLE}
      maxWidth={420}
      actions={
        <button type='button' css={MODAL_BASE_PRIMARY_BUTTON_STYLE} onClick={acknowledge}>
          {MOBILE_WEB_WARNING_ACKNOWLEDGE_LABEL}
        </button>
      }
    >
      <p css={styles.message}>{MOBILE_WEB_WARNING_RISK_TEXT}</p>
      <p css={styles.message}>{MOBILE_WEB_WARNING_GUIDANCE_TEXT}</p>
    </ModalBase>
  );
};
